// assistantFlyResearch.js — THE FIRST THREAD of a personalized sign-up (POB
// S7, PLAN §3; decision 11 of 2026-10-25). After the build board, the AI
// panel's first screen (the Fly start screen, components/AssistantFlyStart.js)
// says what was read and from where, offers three things that wait for the
// person, and keeps a short "Getting started" list — components/
// AssistantFlyResearch.js draws it:
//
//   the summary   "I read <domain> and set up your workspace:" — one line per
//                 fact research found (the server's own words, with the page
//                 it came from as a link), the profile fields it filled;
//                 Review what I read (every page it read; Done confirms the
//                 review), Delete all research, and one line deleted alone
//                 (in-app confirm first, owners and admins)
//   the offers    Website · Team · Imports — nothing acts on its own. Each
//                 offer shows "Soon" and no button until its own read answers
//                 AND its sheet is registered in the page slot (the sheets are
//                 another lane's: `/getting-started/team`,
//                 `/getting-started/imports`). Imports reads the import
//                 CONNECTORS (v0 — not what research found): it opens the
//                 imports sheet, whose drop zone is the `file` connector, only
//                 when `file` is live; one lead, one href, no `?source=`. The
//                 Website offer (S8) shows "Soon" until its lane lands
//   the checklist Review your profile · Website · Invite your team · Import —
//                 SERVER TRUTH only. The build board's `checklist` (each item
//                 { done, at }) is the ONE source when the board carries it.
//                 Without it (an older server): Team ticks when a people row
//                 has `invitedAt`; the three others never tick (a connector
//                 carries no `importedAt`). Read again — the board too — when
//                 the screen opens and when the focus returns. On a phone it
//                 folds into one line, "0 of 4 done".
//
// WHO: the person whose record says `via: 'pob'` (or `origin: 'pob'`) in a
// workspace that HAS a build board (functions/onboardingBuild.js — a 404 there
// is "not this workspace"). THE SIGN-UP RACE (POB lead 2026-10-30): the server
// writes the POB record at sign-up, write-once, and a Fly write can win it —
// so a record that says `fly` (via or origin) counts too, in a workspace whose
// board answered (200). Every other person and workspace: nothing, and the
// start screen keeps its own "Get started" (functions/assistantFlyFirstRun.js
// steps aside only here).
//
// THE DATA IS THE SERVER'S, through the SDK only (feature-detected):
//   the research record  sdk.execute('workspaceProject.records', 'list',
//                        { collection: 'onboarding_research', workspaceId })
//                        → data: { profile, findings, domain }; a Finding is
//                        { key, kind, label: { en, ka }, count?, url, fetchedAt }
//   the people           sdk.listOnboardingPeople(workspaceId)
//                        → { people: [{ id, name, role, sourceUrl, fetchedAt,
//                        expiresAt, invitedAt }] }
//   the import sources   sdk.listImportSources(workspaceId) — the import
//                        CONNECTORS (server core/pob-import; SDK
//                        feat/pob-research 6003611, not on dev yet), in
//                        registry order → { sources: [{ id: 'file' |
//                        'google_sheets', kind, name: { en, ka } | null,
//                        status: 'live' | 'planned' | 'missing', auth }] }.
//                        No label, url or importedAt: research finds come in
//                        a later step, not v0. The block keeps { id, status }.
//                        An SDK without it (3dab11a), or a refused read:
//                        Imports says "Soon"
//   delete               sdk.deleteOnboardingResearch(workspaceId)
//                        → { deleted: { records, people, profileFields } }
//   delete one line      sdk.deleteOnboardingResearchLine(workspaceId, line),
//                        line ∈ profile | offerings | faq | team (PLAN P10)
//   the review confirmed sdk.markOnboardingProfileReviewed(workspaceId)
//                        (POST /core/onboarding/build/profile/reviewed — the
//                        checklist's `profile` item)
// (SDK feat/pob-research 3dab11a, server core/pob-research 34c695c9c; the two
// last methods come with the next SDK and server core/pob-board-2 — until
// then their controls are not there.) The team rows' remove and invite
// (removeOnboardingPerson, inviteOnboardingPerson) belong to the Team sheet,
// not to this block.
// The three reads land in the block's OWN state (never a root key — the
// checklist must never read a flag another surface set).
//
// FRANK: every export is self-contained — no import, no module constant, no
// bare sibling call (`this.call`). `this` is the calling element.

// Is this the workspace of a personalized sign-up, for this person? Their
// record says so AND the build board of this workspace answered. Pure.
export function assistantFlyResearchOwns(root) {
  // a POB record — or a Fly one (the sign-up race): the board decides
  const SIGNUPS = ['pob', 'fly']
  const r = root && typeof root === 'object' ? root : {}
  const prefs = r.userPrefs && typeof r.userPrefs === 'object' ? r.userPrefs : null
  const rec =
    prefs && prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  if (!rec || (SIGNUPS.indexOf(rec.via) === -1 && SIGNUPS.indexOf(rec.origin) === -1)) {
    return false
  }
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const entry = r._pobBuild && typeof r._pobBuild === 'object' ? r._pobBuild : null
  return !!(wsId && entry && entry.ws === wsId && entry.status === 'ok')
}

// THE PRIME — the block's onRender, every stateDeps change (the start screen
// shows again, the workspace changes) and the window's focus coming back
// (`opts.force` — the checklist is read again). Asks nothing while the start
// screen is hidden or for a record that is neither POB nor Fly (the race).
// The build board is read first when this page has not read it (its answer
// says whether this workspace is one) — and on `force` when its answer is
// older than a few seconds (its checklist is the server's), then the block's
// three reads. `el` is the block. → a promise of true when the reads ran.
export function assistantFlyResearchPrime(el, s, opts) {
  const host = el || this
  const o = opts && typeof opts === 'object' ? opts : {}
  const root = (s && s.root) || (host && host.state && host.state.root) || null
  if (!host || typeof host.call !== 'function' || !root) return Promise.resolve(false)
  const fns = (host.context && host.context.functions) || {}
  if (
    typeof fns.assistantFlyStartShown === 'function' &&
    !host.call('assistantFlyStartShown', root)
  ) {
    return Promise.resolve(false)
  }
  const SIGNUPS = ['pob', 'fly']
  // an answer this young is the board as it is (the stateDeps change that its
  // own answer causes asks nothing again)
  const FRESH_MS = 5000
  const prefs = root.userPrefs && typeof root.userPrefs === 'object' ? root.userPrefs : null
  const rec =
    prefs && prefs.onboarding && typeof prefs.onboarding === 'object' ? prefs.onboarding : null
  if (!rec || (SIGNUPS.indexOf(rec.via) === -1 && SIGNUPS.indexOf(rec.origin) === -1)) {
    return Promise.resolve(false)
  }
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  if (!wsId) return Promise.resolve(false)
  const entry = root._pobBuild && typeof root._pobBuild === 'object' ? root._pobBuild : null
  const known = !!(entry && entry.ws === wsId && (entry.status === 'ok' || entry.status === 'none'))
  const stale =
    known &&
    o.force === true &&
    entry.status === 'ok' &&
    !(Date.now() - Number(entry.at || 0) < FRESH_MS)
  const board =
    known && !stale
      ? Promise.resolve(entry)
      : Promise.resolve(host.call('onboardingBuildRead', root, { sdk: o.sdk || null })).catch(
          () => null
        )
  return board.then((got) => {
    if (!got || got.status !== 'ok' || got.ws !== wsId) return false
    return host.call('assistantFlyResearchRead', host, { force: !!o.force, sdk: o.sdk || null })
  })
}

// THE READS — the research record, the people and the import sources, at
// once, into the block's own state:
//   { pobWs, pobReadAt, pobResearch, pobPeopleOk, pobPeople, pobSourcesOk,
//     pobSources, pobBoardChecklist, pobCanReview, pobCanDeleteLine }
// `…Ok` says the offer's read ANSWERED (an SDK without it, or a failure, is
// not an answer: the offer stays "Soon"). `pobBoardChecklist` is the board's
// checklist as this page last read it (the block keeps its own copy, so the
// list moves only with the block's reads); `pobCan…` say this SDK has the
// method a control calls (Done; one line's delete) — without it the control
// is not there. One flight per block; a read of this
// workspace already in the state is enough unless `opts.force`. An answer for
// a workspace that is no longer active drops itself. → a promise of true when
// the state was written.
export function assistantFlyResearchRead(el, opts) {
  const host = el || this
  const o = opts && typeof opts === 'object' ? opts : {}
  const st = host && host.state
  const root = st && st.root
  if (!st || typeof st.update !== 'function' || !root) return Promise.resolve(false)
  const wsOf = (x) => {
    const w = x && x.workspace
    return String((w && (w.id || w._id)) || '')
  }
  const wsId = wsOf(root)
  if (!wsId) return Promise.resolve(false)
  const flight = host.__pobResearchFlight
  if (flight && flight.ws === wsId && flight.promise) return flight.promise
  if (!o.force && st.pobWs === wsId && st.pobReadAt > 0) return Promise.resolve(true)
  const fns = (host.context && host.context.functions) || {}
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = host.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || sdk.isNullClient === true) return Promise.resolve(false)
  const unwrap = (res) => {
    const bare = res && typeof res === 'object' && !Array.isArray(res) ? res : null
    if (bare && bare.success === false) return null
    return bare && 'success' in bare && 'data' in bare ? bare.data : bare
  }
  const web = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u) ? u : '')
  const words = (x) =>
    x && typeof x === 'object'
      ? { en: String(x.en || ''), ka: String(x.ka || '') }
      : typeof x === 'string'
        ? { en: x, ka: x }
        : { en: '', ka: '' }
  const research =
    typeof sdk.execute === 'function'
      ? Promise.resolve()
          .then(() =>
            sdk.execute('workspaceProject.records', 'list', {
              collection: 'onboarding_research',
              workspaceId: wsId,
              limit: 5
            })
          )
          .then(
            (res) => {
              const rows = Array.isArray(res) ? res : res && Array.isArray(res.data) ? res.data : []
              const row = rows.find((x) => x && x.name === 'profile') || rows[0] || null
              const data = row && row.data && typeof row.data === 'object' ? row.data : null
              if (!data) return null
              const profile = data.profile && typeof data.profile === 'object' ? data.profile : {}
              const site = profile.site && typeof profile.site === 'object' ? profile.site : {}
              return {
                domain: String(data.domain || ''),
                siteUrl: web(site.url) || (data.domain ? 'https://' + data.domain + '/' : ''),
                findings: (Array.isArray(data.findings) ? data.findings : [])
                  .filter((f) => f && typeof f.kind === 'string')
                  .map((f) => ({
                    kind: f.kind,
                    label: words(f.label),
                    url: web(f.url)
                  }))
              }
            },
            () => null
          )
      : Promise.resolve(null)
  const people =
    typeof sdk.listOnboardingPeople === 'function'
      ? Promise.resolve()
          .then(() => sdk.listOnboardingPeople(wsId))
          .then(
            (res) => {
              const d = unwrap(res)
              if (!d || !Array.isArray(d.people)) return { ok: false, list: [] }
              return {
                ok: true,
                list: d.people
                  .filter((p) => p && p.id != null)
                  .map((p) => ({
                    id: String(p.id),
                    sourceUrl: web(p.sourceUrl),
                    invited: !!p.invitedAt
                  }))
              }
            },
            () => ({ ok: false, list: [] })
          )
      : Promise.resolve({ ok: false, list: [] })
  const sources =
    typeof sdk.listImportSources === 'function'
      ? Promise.resolve()
          .then(() => sdk.listImportSources(wsId))
          .then(
            (res) => {
              const d = unwrap(res)
              if (!d || !Array.isArray(d.sources)) return { ok: false, list: [] }
              // the import connectors: each one's id and status, nothing else
              return {
                ok: true,
                list: d.sources
                  .filter((x) => x && x.id != null)
                  .map((x) => ({ id: String(x.id), status: String(x.status || '') }))
              }
            },
            () => ({ ok: false, list: [] })
          )
      : Promise.resolve({ ok: false, list: [] })
  const promise = Promise.all([research, people, sources]).then(([a, b, c]) => {
    if (host.__pobResearchFlight && host.__pobResearchFlight.promise === promise) {
      host.__pobResearchFlight = null
    }
    if (wsOf(root) !== wsId) return false
    const entry = root._pobBuild && typeof root._pobBuild === 'object' ? root._pobBuild : null
    const board = entry && entry.ws === wsId && entry.status === 'ok' ? entry.board : null
    const list =
      board && board.checklist && typeof board.checklist === 'object' ? board.checklist : null
    st.update({
      pobWs: wsId,
      pobReadAt: Date.now(),
      pobResearch: a,
      pobPeopleOk: b.ok === true,
      pobPeople: b.list,
      pobSourcesOk: c.ok === true,
      pobSources: c.list,
      pobBoardChecklist: list ? { ...list } : null,
      pobCanReview: typeof sdk.markOnboardingProfileReviewed === 'function',
      pobCanDeleteLine: typeof sdk.deleteOnboardingResearchLine === 'function'
    })
    return true
  })
  host.__pobResearchFlight = { ws: wsId, promise }
  return promise
}

// Shown once its first read of THIS workspace is in (it appears complete, at
// once, and moves nothing after — Rule 66), for a personalized sign-up only.
export function assistantFlyResearchShown(root, st) {
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  if (!wsId || !st || st.pobWs !== wsId || !(st.pobReadAt > 0)) return false
  return !!this.call('assistantFlyResearchOwns', r)
}

// THE SUMMARY — what research read and from where. Pure over the root and the
// block's state (`st`).
//   { show, lead, lines, pages, reviewOpen, reviewText, reviewDoneShown,
//     reviewDoneText, reviewing, deleteShown, deleting, deletedText,
//     failedText }
// A line = { key, text, linkText, href, deleteLine, deleteShown, deleteLabel,
// deleting, actionRow } — the fact in the server's own words, and the page it
// came from. The profile line counts the fields the board says were filled,
// and carries the profile facts research found (locations, hours — profile
// values on the server). `deleteLine` is the server's line key when the line
// can be deleted alone (the profile line → profile, its locations and hours
// with it; a finding of kind offerings → offerings, faq → faq, team /
// teamPage → team; any other kind has none — Delete all research covers
// it); its button shows for owners and admins with an SDK that has the
// method, and then every fact line keeps the button's room (`actionRow`), so
// the rows stay even. `pages` are the pages read (Review what I read); Done
// confirms the review, for the same people, with an SDK that has its method.
export function assistantFlyResearchView(root, st) {
  const HIDDEN = {
    show: false,
    lead: '',
    lines: [],
    pages: [],
    reviewOpen: false,
    reviewText: '',
    reviewDoneShown: false,
    reviewDoneText: '',
    reviewing: false,
    deleteShown: false,
    deleting: false,
    deletedText: '',
    failedText: ''
  }
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  if (!wsId || !st || st.pobWs !== wsId) return HIDDEN
  const word = (key, params) => {
    const raw = el.call('polyglot', key)
    let out = raw == null ? '' : String(raw)
    const p = params && typeof params === 'object' ? params : {}
    for (const token of Object.keys(p)) out = out.split(token).join(String(p[token]))
    return out
  }
  const lang = String(el.call('getActiveLang') || r.lang || 'en')
  const place = (url) => {
    try {
      const u = new URL(url)
      const path = u.pathname && u.pathname !== '/' ? u.pathname : ''
      return u.host.replace(/^www\./, '') + path
    } catch (_) {
      return ''
    }
  }
  const deleted = st.pobDeleted && typeof st.pobDeleted === 'object' ? st.pobDeleted : null
  const lineGone = typeof st.pobLineDeleted === 'string' ? st.pobLineDeleted : ''
  const deletedText = deleted
    ? word('workspace.pobThread.deleted', {
        '{records}': Number(deleted.records) || 0,
        '{people}': Number(deleted.people) || 0,
        '{fields}': Number(deleted.profileFields) || 0
      })
    : lineGone
      ? word('workspace.pobThread.lineDeleted', { '{line}': lineGone })
      : ''
  const failedText =
    st.pobDeleteFailed === true
      ? word('workspace.pobThread.deleteFailed')
      : st.pobReviewFailed === true
        ? word('workspace.pobThread.reviewFailed')
        : ''
  const research = st.pobResearch && typeof st.pobResearch === 'object' ? st.pobResearch : null
  if (!research) {
    return deletedText || failedText ? { ...HIDDEN, show: true, deletedText, failedText } : HIDDEN
  }
  const fns = (el.context && el.context.functions) || {}
  const admin = typeof fns.currentUserIsAdmin === 'function' && !!el.call('currentUserIsAdmin', r)
  // the server's line keys (deleteOnboardingResearchLine), by finding kind.
  // Locations and hours are PROFILE values (CompanyProfile addresses and
  // hours, written by research — the POB lead 2026-10-30): they sit on the
  // profile line, and its delete covers them. Any other kind has no line key.
  const LINES = { offerings: 'offerings', faq: 'faq', team: 'team', teamPage: 'team' }
  const PROFILE_KINDS = ['locations', 'hours']
  const canDeleteLines = admin && st.pobCanDeleteLine === true
  const domain = research.domain || place(research.siteUrl)
  const line = (key, text, url, lineKey) => {
    const at = url ? place(url) : ''
    const del = lineKey || ''
    const shown = !!(del && canDeleteLines)
    return {
      key,
      text,
      linkText: at ? word('workspace.pobThread.from', { '{place}': at }) : '',
      href: at ? url : '',
      deleteLine: del,
      deleteShown: shown,
      deleteLabel: shown ? word('workspace.pobThread.deleteLine', { '{line}': text }) : '',
      deleting: !!(del && st.pobDeletingLine === del),
      actionRow: false
    }
  }
  const lines = []
  const entry =
    r._pobBuild && r._pobBuild.ws === wsId && r._pobBuild.board ? r._pobBuild.board : null
  const profile = entry ? entry.steps.find((x) => x.key === 'profile' && x.status === 'done') : null
  const facts = research.findings.filter((f) => f.kind !== 'page')
  const factText = (f) => f.label[lang] || f.label.en
  const onProfile = (f) => PROFILE_KINDS.indexOf(f.kind) !== -1
  // the profile line: the fields the board says research filled, then the
  // profile facts research found, in the server's words ("Filled 4 fields of
  // your company profile, 2 locations, Opening hours"); with no count, the
  // facts alone, from their page
  const filled =
    profile && profile.count
      ? profile.count === 1
        ? word('workspace.pobThread.profileOne')
        : word('workspace.pobThread.profile', { '{count}': profile.count })
      : ''
  const profileFacts = facts.filter((f) => onProfile(f) && factText(f))
  const profileWords = (filled ? [filled] : []).concat(profileFacts.map(factText))
  if (profileWords.length) {
    lines.push(
      line(
        'pob-read-profile',
        profileWords.join(', '),
        filled ? research.siteUrl : profileFacts[0].url,
        'profile'
      )
    )
  }
  facts.forEach((f, i) => {
    const text = factText(f)
    if (text && !onProfile(f)) {
      lines.push(line('pob-read-' + (i + 1), text, f.url, LINES[f.kind]))
    }
  })
  // one fact line with its own delete: every fact line keeps that room
  const actionRow = lines.some((x) => x.deleteShown)
  for (const x of lines) x.actionRow = actionRow
  const pages = research.findings
    .filter((f) => f.kind === 'page' && f.url)
    .map((f, i) => line('pob-page-' + (i + 1), f.label[lang] || f.label.en || place(f.url), f.url))
  const reviewOpen = st.pobReviewOpen === true
  return {
    show: true,
    lead: domain
      ? word('workspace.pobThread.read', { '{domain}': domain })
      : word('workspace.pobThread.readNoDomain'),
    lines,
    pages,
    reviewOpen,
    reviewText: word(reviewOpen ? 'workspace.pobThread.reviewHide' : 'workspace.pobThread.review'),
    reviewDoneShown: reviewOpen && pages.length > 0 && admin && st.pobCanReview === true,
    reviewDoneText: word('workspace.pobThread.reviewDone'),
    reviewing: st.pobReviewing === true,
    deleteShown: admin,
    deleting: st.pobDeleting === true || !!st.pobDeletingLine,
    deletedText,
    failedText
  }
}

// THE OFFERS — Website · Team · Imports. Pure over the root and the block's
// state. An offer is available only when its read ANSWERED and its sheet is
// registered in the page slot (canOpenInSlot); else it shows "Soon" and no
// button — never a dead button. The Website offer (S8) is the next lane's.
//   { show, lead, rows: [{ key, offer, icon, title, lead, soon, actionText,
//     href }] }
export function assistantFlyOffersView(root, st) {
  const TEAM = '/getting-started/team'
  const IMPORTS = '/getting-started/imports'
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  if (!wsId || !st || st.pobWs !== wsId) return { show: false, lead: '', rows: [] }
  const word = (key, params) => {
    const raw = el.call('polyglot', key)
    let out = raw == null ? '' : String(raw)
    const p = params && typeof params === 'object' ? params : {}
    for (const token of Object.keys(p)) out = out.split(token).join(String(p[token]))
    return out
  }
  const fns = (el.context && el.context.functions) || {}
  const slot = (href) =>
    typeof fns.canOpenInSlot === 'function' && !!el.call('canOpenInSlot', href, el)
  const place = (url) => {
    try {
      const u = new URL(url)
      const path = u.pathname && u.pathname !== '/' ? u.pathname : ''
      return u.host.replace(/^www\./, '') + path
    } catch (_) {
      return ''
    }
  }
  const research = st.pobResearch && typeof st.pobResearch === 'object' ? st.pobResearch : null
  const domain = research ? research.domain || place(research.siteUrl) : ''
  // the Website offer: S8 is not on dev yet — "Soon", no button
  const website = {
    key: 'pob-offer-1',
    offer: 'website',
    icon: 'globe',
    title: word('workspace.pobThread.website'),
    lead: domain
      ? word('workspace.pobThread.websiteLead', { '{domain}': domain })
      : word('workspace.pobThread.websiteLeadNone'),
    soon: true,
    actionText: '',
    href: ''
  }
  // Team: the people found on the company's own team page
  const people = Array.isArray(st.pobPeople) ? st.pobPeople : []
  const teamPage = people.map((p) => place(p.sourceUrl)).find(Boolean) || ''
  const teamOk = st.pobPeopleOk === true && slot(TEAM)
  const team = {
    key: 'pob-offer-2',
    offer: 'team',
    icon: 'userPlus',
    title: word('workspace.pobThread.team'),
    lead:
      people.length && teamPage
        ? people.length === 1
          ? word('workspace.pobThread.teamLeadOne', { '{page}': teamPage })
          : word('workspace.pobThread.teamLead', { '{count}': people.length, '{page}': teamPage })
        : word('workspace.pobThread.teamLeadNone'),
    soon: !teamOk,
    actionText: teamOk ? word('workspace.pobThread.teamAction') : '',
    href: teamOk ? TEAM : ''
  }
  // Imports (v0): the read lists the import CONNECTORS, not what research
  // found. The imports sheet's drop zone is the `file` connector (a connector
  // that is not live is a "Coming soon" row there), so the offer is ready
  // only when the read answered and `file` is live. One lead and one href —
  // the sheet itself, never `?source=` (a connector id is no source).
  const sources = Array.isArray(st.pobSources) ? st.pobSources : []
  const fileLive = sources.some((x) => x && x.id === 'file' && x.status === 'live')
  const importsOk = st.pobSourcesOk === true && fileLive && slot(IMPORTS)
  const imports = {
    key: 'pob-offer-3',
    offer: 'imports',
    icon: 'upload',
    title: word('workspace.pobThread.imports'),
    lead: word('workspace.pobThread.importsLeadNone'),
    soon: !importsOk,
    actionText: importsOk ? word('workspace.pobThread.importsAction') : '',
    href: importsOk ? IMPORTS : ''
  }
  return { show: true, lead: word('workspace.pobThread.offers'), rows: [website, team, imports] }
}

// An offer, pressed: its sheet, by href, in the page slot — the same open as
// every slot entry (openInSlot). Never for an offer that shows "Soon".
export function assistantFlyOfferOpen(s, el) {
  const row = s && typeof s.parse === 'function' ? s.parse() : s
  const host = el || this
  if (!row || row.soon === true || typeof row.href !== 'string' || !row.href) return false
  host.call('openInSlot', row.href, host)
  return true
}

// THE CHECKLIST — "Getting started": four items, ticked by the server's own
// fields only: the board's `checklist` when the board carries one (the ONE
// source — profile: the owner confirmed the review; website: the site step
// done; team: a team row invited; import: v0 never), else the reads' own
// fields (Team: a people row with `invitedAt`; the three others never —
// Import too: the import sources are connectors, and a connector carries no
// `importedAt`). Pure over the root and the block's state.
//   { show, title, progressText, foldText, folded, rows: [{ key, item, title,
//     ticked, stateText }] }
// On a phone (`_isPhoneViewport`) it folds into its one progress line until
// the person opens it (`pobChecklistOpen`, the block's own state).
export function assistantFlyChecklistView(root, st) {
  const el = this
  const r = root && typeof root === 'object' ? root : {}
  const w = r.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const empty = { show: false, title: '', progressText: '', foldText: '', folded: false, rows: [] }
  if (!wsId || !st || st.pobWs !== wsId) return empty
  const word = (key, params) => {
    const raw = el.call('polyglot', key)
    let out = raw == null ? '' : String(raw)
    const p = params && typeof params === 'object' ? params : {}
    for (const token of Object.keys(p)) out = out.split(token).join(String(p[token]))
    return out
  }
  const people = Array.isArray(st.pobPeople) ? st.pobPeople : []
  const server =
    st.pobBoardChecklist && typeof st.pobBoardChecklist === 'object' ? st.pobBoardChecklist : null
  const done = (item) => !!(server[item] && server[item].done === true)
  const items = server
    ? [
        { item: 'profile', title: 'workspace.pobThread.checkProfile', ticked: done('profile') },
        { item: 'website', title: 'workspace.pobThread.checkWebsite', ticked: done('website') },
        { item: 'team', title: 'workspace.pobThread.checkTeam', ticked: done('team') },
        { item: 'import', title: 'workspace.pobThread.checkImport', ticked: done('import') }
      ]
    : [
        // an older server: no field says the profile was reviewed, nor that
        // the website offer was taken, nor that an import ran (the sources
        // are connectors, with no `importedAt`) — these three never tick
        { item: 'profile', title: 'workspace.pobThread.checkProfile', ticked: false },
        { item: 'website', title: 'workspace.pobThread.checkWebsite', ticked: false },
        {
          item: 'team',
          title: 'workspace.pobThread.checkTeam',
          ticked: st.pobPeopleOk === true && people.some((p) => p.invited === true)
        },
        { item: 'import', title: 'workspace.pobThread.checkImport', ticked: false }
      ]
  const rows = items.map((x, i) => ({
    key: 'pob-check-' + (i + 1),
    item: x.item,
    title: word(x.title),
    ticked: x.ticked === true,
    stateText: word(x.ticked ? 'workspace.pobThread.itemDone' : 'workspace.pobThread.itemOpen')
  }))
  const n = rows.filter((x) => x.ticked).length
  const progressText = word('workspace.pobThread.progress', { '{n}': n, '{total}': rows.length })
  const open = st.pobChecklistOpen === true
  return {
    show: true,
    title: word('workspace.pobThread.checklist'),
    progressText,
    foldText: progressText,
    folded: r._isPhoneViewport === true && !open,
    rows
  }
}

// DELETE ALL RESEARCH — asked in the app first (the shell's house confirm,
// CRUD standard D1), then ONE server call (owners and admins; the server
// clears the profile values research wrote and still stand, the research
// records and the people it found). The block then says what went, and the
// board and the reads are asked again. `el` is any element in the block.
export function assistantFlyResearchDelete(el) {
  const host = el || this
  if (!host || typeof host.call !== 'function') return null
  const t = (key) => host.call('polyglot', key)
  return (
    host.call('confirmDialog', {
      title: t('workspace.pobThread.deleteTitle'),
      message: t('workspace.pobThread.deleteMessage'),
      confirmText: t('common.action.delete'),
      cancelText: t('common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => host.call('assistantFlyResearchDeleteRun', host)
    }) || null
  )
}

// The delete itself, after the confirm. `opts.sdk` — a test seam.
// → a promise of the server's counts, or null.
export function assistantFlyResearchDeleteRun(el, opts) {
  const host = el || this
  const o = opts && typeof opts === 'object' ? opts : {}
  const st = host && host.state
  const root = st && st.root
  if (!st || typeof st.update !== 'function' || !root) return Promise.resolve(null)
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (host.context && host.context.functions) || {}
  // one delete at a time (all, or one line)
  if (!wsId || st.pobDeleting === true || st.pobDeletingLine) return Promise.resolve(null)
  if (typeof fns.currentUserIsAdmin !== 'function' || !host.call('currentUserIsAdmin', root)) {
    return Promise.resolve(null)
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = host.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.deleteOnboardingResearch !== 'function') return Promise.resolve(null)
  st.update({ pobDeleting: true, pobDeleteFailed: false, pobReviewFailed: false })
  return Promise.resolve()
    .then(() => sdk.deleteOnboardingResearch(wsId))
    .then((res) => {
      const bare = res && typeof res === 'object' ? res : null
      const d = bare && 'success' in bare && 'data' in bare ? bare.data : bare
      const deleted = d && d.deleted && typeof d.deleted === 'object' ? d.deleted : null
      if (!deleted) throw new Error('shape')
      const counts = {
        records: Number(deleted.records) || 0,
        people: Number(deleted.people) || 0,
        profileFields: Number(deleted.profileFields) || 0
      }
      st.update({
        pobDeleting: false,
        pobDeleted: counts,
        pobLineDeleted: '',
        pobResearch: null,
        pobReviewOpen: false
      })
      // the board's profile and records steps say what went; the offers read
      // again (the people went with the research) — the board first, so the
      // reads keep its fresh checklist
      Promise.resolve(host.call('onboardingBuildRead', root, { sdk: o.sdk || null }))
        .catch(() => null)
        .then(() =>
          host.call('assistantFlyResearchRead', host, { force: true, sdk: o.sdk || null })
        )
        .catch(() => null)
      return counts
    })
    .catch((err) => {
      console.warn('[onboarding] delete research failed:', err && err.message)
      st.update({ pobDeleting: false, pobDeleteFailed: true })
      return null
    })
}

// DELETE ONE LINE (PLAN P10) — a line the server can delete alone (its line
// key: profile | offerings | faq | team), asked in the app first the same way
// as Delete all research (the shell's house confirm, CRUD standard D1), then
// ONE server call. Pressed on the line's own button: the block is found
// through the element tree (`lookup`) and its state holds the run. `el` is the
// pressed element (or the block). → the dialog, or null.
export function assistantFlyResearchDeleteLine(el, line, text) {
  const LINES = ['profile', 'offerings', 'faq', 'team']
  const pressed = el || this
  if (!pressed || LINES.indexOf(line) === -1) return null
  const found = typeof pressed.lookup === 'function' ? pressed.lookup('AssistantFlyResearch') : null
  const host = found || pressed
  if (!host || typeof host.call !== 'function') return null
  const t = (key) => host.call('polyglot', key)
  const words = String(text || '')
  return (
    host.call('confirmDialog', {
      title: t('workspace.pobThread.deleteLineTitle'),
      message: String(t('workspace.pobThread.deleteLineMessage') || '')
        .split('{line}')
        .join(words),
      confirmText: t('common.action.delete'),
      cancelText: t('common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => host.call('assistantFlyResearchDeleteLineRun', host, line, words)
    }) || null
  )
}

// The delete of one line, after the confirm (owners and admins; an SDK with
// `deleteOnboardingResearchLine`). The block then says what went, and the
// board and the reads are asked again (the board first: the reads keep its
// checklist). `opts.sdk` — a test seam. → a promise of true when the server
// took it.
export function assistantFlyResearchDeleteLineRun(el, line, text, opts) {
  const LINES = ['profile', 'offerings', 'faq', 'team']
  const host = el || this
  const o = opts && typeof opts === 'object' ? opts : {}
  const st = host && host.state
  const root = st && st.root
  if (!st || typeof st.update !== 'function' || !root) return Promise.resolve(false)
  if (LINES.indexOf(line) === -1) return Promise.resolve(false)
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (host.context && host.context.functions) || {}
  if (!wsId || st.pobDeleting === true || st.pobDeletingLine) return Promise.resolve(false)
  if (typeof fns.currentUserIsAdmin !== 'function' || !host.call('currentUserIsAdmin', root)) {
    return Promise.resolve(false)
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = host.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.deleteOnboardingResearchLine !== 'function') return Promise.resolve(false)
  const words = String(text || '')
  st.update({
    pobDeletingLine: line,
    pobDeleteFailed: false,
    pobReviewFailed: false,
    pobDeleted: null,
    pobLineDeleted: ''
  })
  return Promise.resolve()
    .then(() => sdk.deleteOnboardingResearchLine(wsId, line))
    .then((res) => {
      const bare = res && typeof res === 'object' ? res : null
      if (bare && bare.success === false) {
        throw new Error(String(bare.error || bare.code || 'refused'))
      }
      st.update({ pobDeletingLine: '', pobLineDeleted: words })
      return Promise.resolve(host.call('onboardingBuildRead', root, { sdk: o.sdk || null }))
        .catch(() => null)
        .then(() =>
          host.call('assistantFlyResearchRead', host, { force: true, sdk: o.sdk || null })
        )
        .catch(() => null)
        .then(() => true)
    })
    .catch((err) => {
      console.warn('[onboarding] delete research line failed:', err && err.message)
      st.update({ pobDeletingLine: '', pobDeleteFailed: true })
      return false
    })
}

// THE REVIEW, CONFIRMED — Done under "Review what I read". The review closes;
// for an owner or admin with an SDK that has `markOnboardingProfileReviewed`,
// the server hears that the owner reviewed what research read (the checklist's
// `profile` item — the server ticks it, never this page). Then the board (its
// checklist) and the reads again. A failure says so in one line. `el` is the
// pressed Done (or the block). `opts.sdk` — a test seam. → a promise of true
// when the server took it.
export function assistantFlyResearchReviewDone(el, opts) {
  const pressed = el || this
  const o = opts && typeof opts === 'object' ? opts : {}
  const found =
    pressed && typeof pressed.lookup === 'function' ? pressed.lookup('AssistantFlyResearch') : null
  const host = found || pressed
  const st = host && host.state
  const root = st && st.root
  if (!st || typeof st.update !== 'function' || !root) return Promise.resolve(false)
  st.update({ pobReviewOpen: false })
  const w = root.workspace
  const wsId = String((w && (w.id || w._id)) || '')
  const fns = (host.context && host.context.functions) || {}
  if (!wsId || st.pobReviewing === true) return Promise.resolve(false)
  if (typeof fns.currentUserIsAdmin !== 'function' || !host.call('currentUserIsAdmin', root)) {
    return Promise.resolve(false)
  }
  let sdk = o.sdk || null
  if (!sdk && typeof fns.getSDK === 'function') {
    try {
      sdk = host.call('getSDK')
    } catch (_) {
      sdk = null
    }
  }
  if (!sdk || typeof sdk.markOnboardingProfileReviewed !== 'function') {
    return Promise.resolve(false)
  }
  st.update({ pobReviewing: true, pobReviewFailed: false, pobDeleteFailed: false })
  return Promise.resolve()
    .then(() => sdk.markOnboardingProfileReviewed(wsId))
    .then((res) => {
      const bare = res && typeof res === 'object' ? res : null
      if (bare && bare.success === false) {
        throw new Error(String(bare.error || bare.code || 'refused'))
      }
      st.update({ pobReviewing: false })
      return Promise.resolve(host.call('onboardingBuildRead', root, { sdk: o.sdk || null }))
        .catch(() => null)
        .then(() =>
          host.call('assistantFlyResearchRead', host, { force: true, sdk: o.sdk || null })
        )
        .catch(() => null)
        .then(() => true)
    })
    .catch((err) => {
      console.warn('[onboarding] the review was not saved:', err && err.message)
      st.update({ pobReviewing: false, pobReviewFailed: true })
      return false
    })
}
